import React from 'react';
import { Box, Button, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import type { DashboardMealPlanActionItem } from '@/lib/api/dashboard';
import { ClientNameLink } from '@/components/clients/ClientNameLink';

import cupIcon from '@/assets/images/cup.svg';

import {
  CS_PRIMARY,
  DASHBOARD_TODAY_CARD_EMPTY_MIN_HEIGHT,
  DASHBOARD_TODAY_EMPTY_STATE_CONTAINER_SX,
  DASHBOARD_TODAY_EMPTY_STATE_TEXT_SX,
} from './dashboardHomeTokens';
import { formatDashboardDateRange } from './dashboardHomeFormatters';

const EXPIRED_PROFILE_COLOR = '#e11d48';

const MealPlanExpiryBadgeLabel: React.FC<{ label: string }> = ({ label }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.65, lineHeight: 1 }}>
    <Box
      component="span"
      sx={{
        width: 5,
        height: 5,
        borderRadius: '999px',
        bgcolor: '#fff',
        display: 'block',
        flexShrink: 0,
      }}
    />
    <Box component="span" sx={{ lineHeight: '16px' }}>{label}</Box>
  </Box>
);

export interface DashboardMealPlansCardProps {
  items?: DashboardMealPlanActionItem[];
  isLoading?: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  onViewAll?: () => void;
  onReassignPlan?: (row: DashboardMealPlanActionItem) => void;
}

export const DashboardMealPlansCard: React.FC<DashboardMealPlansCardProps> = ({
  items = [],
  isLoading = false,
  expanded,
  onToggleExpanded,
  onViewAll,
  onReassignPlan,
}) => {
  const visibleItems = expanded ? items : items.slice(0, 2);
  const isEmpty = !isLoading && visibleItems.length === 0;

  const getBadge = (row: DashboardMealPlanActionItem) => {
    if (row.type === 'expired') {
      return {
        label: row.daysExpired ? `Expired ${row.daysExpired}d` : 'Expired',
        bg: EXPIRED_PROFILE_COLOR,
      };
    }
    const hours = row.hoursUntilExpiry;
    if (typeof hours === 'number') {
      if (hours <= 24) return { label: 'Expires Today', bg: '#ffa2a2' };
      if (hours <= 48) return { label: 'Expires Tomorrow', bg: '#faa60b' };
      return { label: `${hours}h left`, bg: '#faa60b' };
    }
    return { label: 'Expiring Soon', bg: '#faa60b' };
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid #ebebeb',
        overflow: 'hidden',
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        ...(isEmpty ? { minHeight: DASHBOARD_TODAY_CARD_EMPTY_MIN_HEIGHT } : {}),
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ borderBottom: '1px solid #f2f2f2', px: 2.5, py: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box component="img" src={cupIcon} alt="" aria-hidden sx={{ width: 26, height: 26, objectFit: 'contain', display: 'block' }} />
          <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem', color: '#1a1a1a' }}>Meal Plans</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Button
            variant="text"
            size="small"
            onClick={onViewAll}
            endIcon={<ChevronRightRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: CS_PRIMARY,
              minWidth: 0,
            }}
          >
            View all
          </Button>
          <IconButton size="small" aria-label={expanded ? 'Collapse panel' : 'Expand panel'} onClick={onToggleExpanded}>
            {expanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
          </IconButton>
        </Stack>
      </Stack>
      <Box sx={isEmpty ? DASHBOARD_TODAY_EMPTY_STATE_CONTAINER_SX : undefined}>
          {isLoading && items.length === 0 && (
            <Typography sx={{ px: 2, py: 2, fontSize: 12, color: '#4a5565' }}>Loading meal plans...</Typography>
          )}
          {isEmpty && (
            <Typography sx={DASHBOARD_TODAY_EMPTY_STATE_TEXT_SX}>
              No meal plans expiring in the next 72 hours or recently expired.
            </Typography>
          )}
          {visibleItems.map((row, index, arr) => {
            const badge = getBadge(row);
            const isExpired = row.type === 'expired';
            const avatarBg = isExpired ? '#fff1f2' : '#fff5ec';
            const avatarColor = isExpired ? EXPIRED_PROFILE_COLOR : '#ea580c';
            const planName = row.planName || 'Meal plan';

            return (
              <React.Fragment key={row.id}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} sx={{ px: 2, py: 1.5 }}>
                  <Stack direction="row" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 47,
                        height: 47,
                        borderRadius: '13px',
                        bgcolor: avatarBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <PersonRoundedIcon sx={{ color: avatarColor, fontSize: 30 }} />
                    </Box>
                    <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', color: '#1a1a1a' }}>
                          <ClientNameLink clientId={row.clientId} className="text-[#1a1a1a] hover:text-[#1a1a1a]">
                            {row.clientName}
                          </ClientNameLink>
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            bgcolor: badge.bg,
                            color: '#fff',
                            borderRadius: 999,
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: 26,
                            px: 1,
                            py: 0,
                            fontSize: 12,
                            fontWeight: 600,
                            lineHeight: 1,
                          }}
                        >
                          <MealPlanExpiryBadgeLabel label={badge.label} />
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ fontSize: '0.75rem', color: '#000' }}>
                        <Typography component="span" sx={{ fontSize: 'inherit' }}>
                          Plan: {planName}
                        </Typography>
                        <Typography component="span">·</Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: '#000' }}>
                          <CalendarMonthRoundedIcon sx={{ fontSize: 16, color: '#000' }} />
                          <Typography component="span" sx={{ fontSize: 'inherit' }}>
                            {formatDashboardDateRange(row.startDate, row.endDate)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddRoundedIcon />}
                    disabled={!row.clientId?.trim() || !onReassignPlan}
                    title={
                      !row.clientId?.trim()
                        ? 'This row has no linked client; open the client from the list to add a meal plan.'
                        : undefined
                    }
                    onClick={() => onReassignPlan?.(row)}
                    sx={{
                      borderRadius: '7px',
                      height: 30,
                      minWidth: 126,
                      fontSize: '0.75rem',
                      borderColor: '#e2e8f0',
                      color: '#000',
                      textTransform: 'none',
                      alignSelf: { xs: 'stretch', sm: 'center' },
                    }}
                  >
                    Re-assign plan
                  </Button>
                </Stack>
                {index < arr.length - 1 && <Divider sx={{ borderColor: '#f5f5f5' }} />}
              </React.Fragment>
            );
          })}
      </Box>
    </Paper>
  );
};
